import { useEffect, useMemo } from 'react';

import { useStores } from './useStores';

import useFetchedEstimate from '~/core/hooks/useFetchedEstimate';
import { createSelectedHook } from '~/core/utils';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { selectLoadEstimateCode } from '~/redux/slices/estimate';
import {
  selectRestorableStoreId,
  selectSelectedStore,
  updateSelectedStore,
} from '~/redux/slices/store';

export const useSelectedStore = () => {
  const currentSelectedStore = useAppSelector(selectSelectedStore);
  const restorableStoreId = useAppSelector(selectRestorableStoreId);
  const loadEstimateCode = useAppSelector(selectLoadEstimateCode);
  const { stores } = useStores();
  const { fetchedEstimate } = useFetchedEstimate();

  // The authoritative Lead store ID: prefer restorableStoreId (set by the full
  // restore flow) but fall back to the fetched estimate's storeId directly.
  // This fallback is necessary because restoreFromFetchedEstimate may not run
  // (the main loading effect's dedup guard can block it when
  // currentEstimateCode is set before the restore fires).
  const leadStoreId = restorableStoreId ?? fetchedEstimate?.storeId;

  const { selectedValue: computedStore } = createSelectedHook({
    selector: selectSelectedStore,
    restorableSelector: selectRestorableStoreId,
    apiQueryHook: useStores,
    selectRestorableValue: ({
      apiHookResponse,
      restorableValue,
      selectedValue,
    }) =>
      selectedValue ||
      apiHookResponse.stores.find(
        (store) => store.companyId === restorableValue
      ),
    shouldReturnDefault: ({ selectedValue, apiHookResponse }) =>
      !selectedValue ||
      !apiHookResponse.stores.find(
        (store) => store.companyId === selectedValue.companyId
      ),
    selectDefaultValue: ({ apiHookResponse }) => apiHookResponse.stores[0],
  })();

  // If the user already committed a deliberate choice to Redux, don't override.
  const userHasManuallySelectedStore = useMemo(
    () =>
      currentSelectedStore != null &&
      currentSelectedStore.companyId !== leadStoreId &&
      stores.some((s) => s.companyId === currentSelectedStore.companyId),
    [currentSelectedStore, leadStoreId, stores]
  );

  // Prong 1: Make the Lead's store win at compute time — but only when the
  // user hasn't manually selected a different store. If currentSelectedStore is
  // already a valid in-list store that differs from the Lead's store, it means
  // the user explicitly picked it (via useStoreSelection) — respect that choice.
  const selectedStore = useMemo(() => {
    const shouldUseLeadStore =
      computedStore?.companyId !== leadStoreId && !userHasManuallySelectedStore;
    const leadStore = shouldUseLeadStore
      ? stores.find((s) => s.companyId === leadStoreId)
      : undefined;

    return leadStore ?? computedStore;
  }, [computedStore, leadStoreId, stores, userHasManuallySelectedStore]);

  const dispatch = useAppDispatch();

  // Prong 2: Guard the write-back so the distance-default is never persisted
  // while a Lead restore is in progress.
  useEffect(() => {
    if (
      selectedStore &&
      selectedStore.companyId !== currentSelectedStore?.companyId
    ) {
      // Block write-back while a Lead restore is expected but hasn't completed:
      // 1. Estimate is loading but the Lead's store hasn't been determined yet
      const isRestoreExpected = !!loadEstimateCode && !leadStoreId;
      // 2. Lead store is known, computed store doesn't match, the Lead's
      //    store is achievable, AND the user hasn't manually selected a
      //    different store (manual selection goes through useStoreSelection
      //    which dispatches updateSelectedStore directly).
      const isRestorePending =
        !!leadStoreId &&
        selectedStore.companyId !== leadStoreId &&
        stores.some((s) => s.companyId === leadStoreId) &&
        !userHasManuallySelectedStore;

      if (!isRestoreExpected && !isRestorePending) {
        dispatch(updateSelectedStore(selectedStore));
      }
    }
  }, [
    selectedStore,
    dispatch,
    currentSelectedStore,
    leadStoreId,
    loadEstimateCode,
    stores,
    userHasManuallySelectedStore,
  ]);

  return selectedStore;
};
